import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SlidersHorizontalIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { ColorOverride, FieldDefinition, FieldSpan, HighlightTone, LayoutSettings, LoanRecord, PanelField, RuleMatch, ToneColors } from '../../types/highlights';
import { neutralChip, styleForTone, toneLabels, toneOrder } from '../../utils/palette';
import { resolveSpan, spanOptions, textScaleStyles, truncateValue } from '../../utils/layout';
interface HighlightChipProps {
  definition: FieldDefinition;
  field: PanelField;
  loan: LoanRecord;
  index: number;
  total: number;
  toneColors: ToneColors;
  layout: LayoutSettings;
  match: RuleMatch | null;
  ruleCount: number;
  isDragTarget: boolean;
  onDragStartChip: (index: number) => void;
  onDragOverChip: (index: number) => void;
  onDropChip: (index: number) => void;
  onDragEndChip: () => void;
  onMove: (from: number, to: number) => void;
  onRemove: (fieldId: string) => void;
  onSetColorOverride: (fieldId: string, colorOverride?: ColorOverride) => void;
  onSetSpan: (fieldId: string, span: FieldSpan) => void;
  onSetMaxChars: (fieldId: string, maxChars?: number) => void;
  onManageRules: (fieldId: string) => void;
}
export function HighlightChip({
  definition,
  field,
  loan,
  index,
  total,
  toneColors,
  layout,
  match,
  ruleCount,
  isDragTarget,
  onDragStartChip,
  onDragOverChip,
  onDropChip,
  onDragEndChip,
  onMove,
  onRemove,
  onSetColorOverride,
  onSetSpan,
  onSetMaxChars,
  onManageRules
}: HighlightChipProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);
  const {
    colorOverride,
    span,
    maxChars
  } = field;
  const tone: HighlightTone | null = colorOverride === 'none' ? null : colorOverride ? colorOverride : match ? match.tone : null;
  const styles = tone ? styleForTone(tone, toneColors) : neutralChip;
  const scale = textScaleStyles[layout.textScale];
  const fullValue = definition.getValue(loan);
  const displayValue = truncateValue(fullValue, maxChars);
  const isEmpty = fullValue === '—';
  const showFlag = Boolean(match?.flag) && !colorOverride && tone !== null;
  const columns = resolveSpan(span, fullValue.length, layout);
  return <div ref={containerRef} className="relative min-w-0" style={{
    gridColumn: `span ${columns} / span ${columns}`
  }} data-id="element-15" data-mp="1u4fug8o7sjo3">
      <div draggable onDragStart={event => {
      event.dataTransfer.effectAllowed = 'move';
      onDragStartChip(index);
    }} onDragOver={event => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      onDragOverChip(index);
    }} onDrop={event => {
      event.preventDefault();
      onDropChip(index);
    }} onDragEnd={onDragEndChip} className={['flex w-full cursor-grab items-center gap-1.5 whitespace-nowrap rounded border transition active:cursor-grabbing', scale.height, scale.padding, styles.chip, isDragTarget ? 'ring-2 ring-[#0b5cab] ring-offset-1' : ''].join(' ')} data-id="element-16" data-mp="3hrk8zm3r0u8c">
        <button type="button" onClick={() => setMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={menuOpen} aria-label={`${definition.label}: ${fullValue}. Field options`} title={match?.reason ?? fullValue} className="flex min-w-0 flex-1 items-center gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-17" data-mp="ialzumo76yw6">
          <span className="flex min-w-0 flex-1 flex-col leading-tight" data-id="element-18" data-mp="ef9i1sv0rjc6">
            <span className={['truncate font-semibold uppercase tracking-wide', scale.labelText, tone ? styles.text : 'text-slate-500'].join(' ')} data-id="element-19" data-mp="ei5hxmqfgi39">
              {definition.label}
            </span>
            <span className={['truncate font-bold', scale.valueText, isEmpty ? 'font-semibold text-slate-400' : styles.text].join(' ')} data-id="element-20" data-mp="mbjjxna5b4e">
              {displayValue}
            </span>
          </span>
          {showFlag && match?.flag ? <span className={`flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-px font-bold ${scale.flagText} ${styles.flag}`} data-id="element-21" data-mp="1eng7rjo6f4rc">
              <TriangleAlertIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-22" data-mp="1jkzn6mwm1nkv" />
              {match.flag}
            </span> : null}
        </button>

        <button type="button" onClick={() => onRemove(definition.id)} aria-label={`Remove ${definition.label} from highlights`} className={['ml-0.5 shrink-0 border-l pl-1.5 text-slate-400 transition hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', styles.divider].join(' ')} data-id="element-23" data-mp="19pxvpkev49f4">
          <XIcon className="h-3 w-3" data-id="element-24" data-mp="1smfx6tjijnok" />
        </button>
      </div>

      {match ? <span className="sr-only" data-id="element-25" data-mp="2gctjs4jbwubr">{match.reason}</span> : null}

      {menuOpen ? <div role="menu" aria-label={`${definition.label} settings`} className="absolute left-0 top-[calc(100%+6px)] z-30 w-64 rounded-md border border-slate-200 bg-white p-2 text-left shadow-lg" data-id="element-26" data-mp="2l3tdz212cyak">
          <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-27" data-mp="2qzrsw3ryute5">
            Highlight
          </p>
          <div className="flex items-center gap-1.5 px-1 pb-2" data-id="element-28" data-mp="2m16pxwlbxvm0">
            {toneOrder.map(toneOption => {
          const active = colorOverride === toneOption;
          return <button key={toneOption} type="button" role="menuitemradio" aria-checked={active} aria-label={toneLabels[toneOption]} title={toneLabels[toneOption]} onClick={() => onSetColorOverride(definition.id, toneOption)} className={['h-6 w-6 rounded border-2 transition', styleForTone(toneOption, toneColors).swatch, active ? 'border-slate-900' : 'border-white hover:border-slate-300'].join(' ')} data-id="element-29" data-mp="2c3aef3ohcynd" />;
        })}
            <button type="button" role="menuitemradio" aria-checked={colorOverride === 'none'} aria-label="No highlight" title="No highlight" onClick={() => onSetColorOverride(definition.id, 'none')} className={['h-6 w-6 rounded border-2 bg-white transition', colorOverride === 'none' ? 'border-slate-900' : 'border-slate-200 hover:border-slate-400'].join(' ')} data-id="element-30" data-mp="1j56lt4weca9c" />
          </div>

          {colorOverride !== undefined ? <button type="button" role="menuitem" onClick={() => onSetColorOverride(definition.id, undefined)} className="mb-1 w-full rounded px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50" data-id="element-31" data-mp="1a864tjbnpefx">
              Clear manual color
            </button> : null}

          <div className="my-1 border-t border-slate-100" data-id="element-32" data-mp="1rvf21ogiylyb" />

          <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-33" data-mp="25uett4zc3gyp">
            Rules
          </p>
          <p className="px-1 pb-1.5 text-[11px] leading-snug text-slate-500" data-id="element-34" data-mp="20v26kj4jw7vl">
            {ruleCount === 0 ? 'No rules on this field yet.' : match ? `Matching: ${match.reason}` : `${ruleCount} rule${ruleCount === 1 ? '' : 's'}, none matching now.`}
          </p>
          <button type="button" role="menuitem" onClick={() => {
        setMenuOpen(false);
        onManageRules(definition.id);
      }} className="mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] font-medium text-[#0b5cab] hover:bg-[#f3f8ff]" data-id="element-35" data-mp="2ojl72chczgd4">
            <SlidersHorizontalIcon className="h-3.5 w-3.5" data-id="element-36" data-mp="3d62bu51meyax" />
            {ruleCount === 0 ? 'Create a rule' : 'Edit rules'}
          </button>

          <div className="my-1 border-t border-slate-100" data-id="element-37" data-mp="3h6334q9tj3ze" />

          <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-38" data-mp="337v5sh8ve0np">
            Width
          </p>
          <div role="radiogroup" aria-label="Field width" className="flex items-center gap-1 px-1 pb-1" data-id="element-39" data-mp="385ekfi9hcfg6">
            {spanOptions.map(option => {
          const active = (span ?? 'auto') === option.value && !layout.uniformSizing;
          return <button key={String(option.value)} type="button" role="radio" aria-checked={active} disabled={layout.uniformSizing} onClick={() => onSetSpan(definition.id, option.value)} className={['flex-1 rounded border px-1.5 py-1 text-[12px] font-medium transition', active ? 'border-[#0b5cab] bg-[#f3f8ff] text-[#0b5cab]' : 'border-slate-200 text-slate-600 hover:bg-slate-50', layout.uniformSizing ? 'cursor-not-allowed opacity-50' : ''].join(' ')} data-id="element-40" data-mp="2kion2u82t91w">
                  {option.label}
                </button>;
        })}
          </div>
          <p className="px-1 pb-2 text-[11px] leading-snug text-slate-500" data-id="element-41" data-mp="3o8u8bgkt3g8g">
            {layout.uniformSizing ? 'Uniform sizing is on — turn it off under Configure to set widths.' : `Auto fits the value (${fullValue.length} characters → ${columns} column${columns === 1 ? '' : 's'}).`}
          </p>

          <label htmlFor={`maxchars-${definition.id}`} className="block px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-42" data-mp="azg360arun4v">
            Max characters
          </label>
          <div className="flex items-center gap-2 px-1 pb-1" data-id="element-43" data-mp="20fsgb8v8dwwt">
            <input id={`maxchars-${definition.id}`} type="number" min={4} max={80} value={maxChars ?? ''} placeholder="Full value" onChange={event => {
          const next = Number(event.target.value);
          onSetMaxChars(definition.id, event.target.value === '' || Number.isNaN(next) ? undefined : next);
        }} className="w-24 rounded border border-slate-200 px-2 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-44" data-mp="1via2b42cwrhy" />
            <button type="button" disabled={maxChars === undefined} onClick={() => onSetMaxChars(definition.id, undefined)} className="rounded px-1.5 py-1 text-[12px] font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-45" data-mp="1mfr4wzhcb733">
              Clear
            </button>
          </div>
          <p className="px-1 pb-2 text-[11px] leading-snug text-slate-500" data-id="element-46" data-mp="1zj3idvjokvyp">
            Value is {fullValue.length} characters long.
          </p>

          <div className="my-1 border-t border-slate-100" data-id="element-47" data-mp="28l2x2h783nyh" />

          <button type="button" role="menuitem" disabled={index === 0} onClick={() => onMove(index, index - 1)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-48" data-mp="28kjo3pzjw9wl">
            <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-49" data-mp="1pmqlypwr4409" /> Move left
          </button>
          <button type="button" role="menuitem" disabled={index === total - 1} onClick={() => onMove(index, index + 1)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-50" data-mp="t4uggw8lu3ga">
            <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-51" data-mp="klhave8z0ffz" /> Move right
          </button>
        </div> : null}
    </div>;
}